/**
 * Auth Store — React Context + useReducer
 *
 * Manages:
 *  - user (decoded from JWT or fetched from /api/v1/me)
 *  - token (stored in localStorage under trainova_access_token)
 *  - isAuthenticated
 *  - isLoading
 *
 * Rules:
 *  - JWT parsing is centralized here — no component parses JWTs directly.
 *  - Token storage/retrieval uses the shared TOKEN_KEY constant via api.ts helpers.
 *  - Existing JWT shape is preserved: { userId, email, role, tenantId, tenantSlug }
 */

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  auth,
  setStoredToken,
  setStoredRefreshToken,
  clearStoredTokens,
  getStoredToken,
} from '../services/api';
import { clearDashboardCache } from '../pages/Dashboard/DashboardPage';

// ---------------------------------------------------------------------------
// Types — mirror existing JWT payload shape
// ---------------------------------------------------------------------------

export interface AuthUser {
  userId: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role: string; // existing singular role: "LEARNER" | "ADMIN" | "SUPER_ADMIN" | "INSTRUCTOR"
  tenantId: string;
  tenantSlug?: string;
  preferredLanguage?: string;
  // Future: roles?: string[]; permissions?: string[];
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_LOADING' }
  | { type: 'AUTH_SUCCESS'; payload: { user: AuthUser; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_RESTORE'; payload: { user: AuthUser; token: string } }
  | { type: 'AUTH_UPDATE_LANGUAGE'; payload: string }
  | { type: 'AUTH_CLEAR_ERROR' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Decode a JWT payload without verification.
 * Verification happens server-side — this is only for displaying user info.
 */
function decodeJwtPayload(token: string): AuthUser | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.userId && !payload.id) return null;
    const firstName = payload.firstName;
    const lastName = payload.lastName;
    const name = payload.name ?? (firstName && lastName ? `${firstName} ${lastName}` : firstName || undefined);

    return {
      userId: payload.userId ?? payload.id,
      email: payload.email ?? '',
      name,
      firstName,
      lastName,
      role: payload.role ?? 'LEARNER',
      tenantId: payload.tenantId ?? '',
      tenantSlug: payload.tenantSlug,
      preferredLanguage: payload.preferredLanguage,
    };
  } catch {
    return null;
  }
}

/**
 * Check whether a JWT token has expired based on its standard exp claim.
 * Returns true if expired or malformed, false if still valid.
 */
function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return false;
    const nowSec = Math.floor(Date.now() / 1000);
    return nowSec >= payload.exp;
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true, // start loading so we can check localStorage
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
    case 'AUTH_RESTORE':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    case 'AUTH_UPDATE_LANGUAGE':
      return {
        ...state,
        user: state.user ? { ...state.user, preferredLanguage: action.payload } : null,
      };
    case 'AUTH_CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, tenantSlug?: string, rememberMe?: boolean) => Promise<boolean>;
  setSession: (token: string, refreshToken?: string) => AuthUser | null;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUserLanguage: (language: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Restore session from localStorage on mount — with client-side expiry pre-check
  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      dispatch({ type: 'AUTH_LOGOUT' });
      return;
    }

    // Pre-check JWT expiry: avoid issuing unneeded 401 network requests on dead tokens
    if (isJwtExpired(token)) {
      clearStoredTokens();
      dispatch({ type: 'AUTH_LOGOUT' });
      return;
    }

    // Prefer server-side verification (GET /api/v1/auth/me)
    // Falls back to client-side JWT decode if server unreachable (offline/cold start)
    auth
      .me()
      .then((res) => {
        if (res.success && res.data) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const serverUser = res.data as any;
          const firstName = serverUser.firstName || '';
          const lastName = serverUser.lastName || '';
          const name = serverUser.name || (firstName && lastName ? `${firstName} ${lastName}` : firstName || serverUser.email?.split('@')[0]);

          const user: AuthUser = {
            userId: serverUser.userId,
            email: serverUser.email,
            name,
            firstName: serverUser.firstName,
            lastName: serverUser.lastName,
            role: serverUser.role,
            tenantId: serverUser.tenantId,
            tenantSlug: serverUser.tenantSlug,
            preferredLanguage: serverUser.preferredLanguage,
          };
          dispatch({ type: 'AUTH_RESTORE', payload: { user, token } });
        } else {
          // Token rejected by server — clear it cleanly
          clearStoredTokens();
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      })
      .catch(() => {
        // Server unreachable — fall back to client-side decode (graceful degradation)
        const user = decodeJwtPayload(token);
        if (user && !isJwtExpired(token)) {
          dispatch({ type: 'AUTH_RESTORE', payload: { user, token } });
        } else {
          clearStoredTokens();
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      });

    const handleAuthExpired = () => {
      dispatch({ type: 'AUTH_LOGOUT' });
    };

    window.addEventListener('trainova:auth_expired', handleAuthExpired);
    return () => {
      window.removeEventListener('trainova:auth_expired', handleAuthExpired);
    };
  }, []);

  const setSession = useCallback((token: string, refreshToken?: string): AuthUser | null => {
    const user = decodeJwtPayload(token);
    if (!user) {
      dispatch({ type: 'AUTH_FAILURE', payload: 'Invalid authentication token.' });
      return null;
    }
    setStoredToken(token);
    if (refreshToken) {
      setStoredRefreshToken(refreshToken);
    }
    dispatch({ type: 'AUTH_SUCCESS', payload: { user, token } });
    return user;
  }, []);

  const login = useCallback(
    async (email: string, password: string, tenantSlug?: string, rememberMe = false): Promise<boolean> => {
      dispatch({ type: 'AUTH_LOADING' });

      const res = await auth.login({ email, password, tenantSlug, rememberMe });

      if (res.success && res.data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = res.data as any;
        const token: string = data.token ?? data.accessToken ?? data.access_token ?? '';
        if (!token) {
          dispatch({ type: 'AUTH_FAILURE', payload: 'Invalid response from server.' });
          return false;
        }

        const decoded = decodeJwtPayload(token);
        if (!decoded) {
          dispatch({ type: 'AUTH_FAILURE', payload: 'Invalid authentication token.' });
          return false;
        }

        const rawUser = data.user || {};
        const firstName = rawUser.firstName || decoded.firstName || '';
        const lastName = rawUser.lastName || decoded.lastName || '';
        const name = rawUser.name || (firstName && lastName ? `${firstName} ${lastName}` : firstName || decoded.name || decoded.email?.split('@')[0]);

        const user: AuthUser = {
          ...decoded,
          name,
          firstName: rawUser.firstName || decoded.firstName,
          lastName: rawUser.lastName || decoded.lastName,
          preferredLanguage: rawUser.preferredLanguage || decoded.preferredLanguage,
        };

        setStoredToken(token);
        if (data.refreshToken) {
          setStoredRefreshToken(data.refreshToken);
        }
        dispatch({ type: 'AUTH_SUCCESS', payload: { user, token } });
        return true;
      } else {
        dispatch({
          type: 'AUTH_FAILURE',
          payload: res.error?.message ?? 'Login failed. Please check your credentials.',
        });
        return false;
      }
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } catch {
      // best-effort logout
    }
    clearStoredTokens();
    clearDashboardCache();
    dispatch({ type: 'AUTH_LOGOUT' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' });
  }, []);

  const updateUserLanguage = useCallback((language: string) => {
    dispatch({ type: 'AUTH_UPDATE_LANGUAGE', payload: language });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, setSession, logout, clearError, updateUserLanguage }}>
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
